import React, { useEffect, useRef } from 'react';
import { prefersReducedMotion, readVar, useThemeVersion } from './hooks';

// Hero art: sources on the left send items through a field of signals to the entities they
// concern; now and then an entity's pattern holds, it turns amber and fires an action.
// Colours come from the page's hue tokens, so it follows the light/dark theme.
const SOURCES = [
  { label: 'Email', hue: 6 }, { label: 'Tickets', hue: 4 }, { label: 'Chat', hue: 5 },
  { label: 'Logs', hue: 8 }, { label: 'Orders', hue: 7 }, { label: 'Invoices', hue: 2 },
];
const ENTITIES = 4;

function rng(seed) {
  let s = seed >>> 0;
  return () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
}

export default function Constellation() {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const theme = useThemeVersion();

  useEffect(() => {
    const wrap = wrapRef.current; const canvas = canvasRef.current;
    if (!wrap || !canvas) return undefined;
    const ctx = canvas.getContext('2d');
    const reduced = prefersReducedMotion();
    const r = rng(7);
    const col = {};
    const readColours = () => {
      for (let i = 1; i <= 8; i++) col[i] = readVar(wrap, `--kz-h${i}`, '#3fe0c5');
      col.text = readVar(wrap, '--kz-text-2', '#aab6ca');
      col.muted = readVar(wrap, '--kz-muted', '#8290a8');
      col.line = readVar(wrap, '--kz-art-line', 'rgba(148,170,210,0.18)');
      col.light = document.documentElement.getAttribute('data-wx-theme') === 'light';
    };
    readColours();

    let W = 0; let H = 0; let dpr = 1;
    let stars = []; let src = []; let ents = []; let trig = null; let act = null;
    const layout = () => {
      W = wrap.clientWidth; H = wrap.clientHeight; dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      const narrow = W < 520;
      const top = 34; const bot = H - 18;
      src = SOURCES.map((s, i) => ({ ...s, x: W * (narrow ? 0.1 : 0.09), y: top + ((i + 0.5) * (bot - top)) / SOURCES.length }));
      const rs = rng(11);
      stars = Array.from({ length: narrow ? 26 : 42 }, () => ({
        x: W * (0.26 + rs() * 0.36), y: top + 10 + rs() * (bot - top - 20), p: rs() * Math.PI * 2, s: 0.8 + rs() * 1.6,
      }));
      ents = Array.from({ length: ENTITIES }, (_, i) => ({
        x: W * (narrow ? 0.72 : 0.72), y: top + ((i + 0.5) * (bot - top)) / ENTITIES,
        risk: 0.2 + 0.1 * i, pulse: 0, hot: 0,
      }));
      trig = { x: W * 0.88, y: (top + bot) / 2 - (bot - top) * 0.16 };
      act = { x: W * 0.88, y: (top + bot) / 2 + (bot - top) * 0.16 };
    };
    layout();

    const parts = []; const beams = []; let spawnT = 0; let fireT = 5; let last = performance.now();
    const spawn = () => {
      const s = src[Math.floor(r() * src.length)]; const via = stars[Math.floor(r() * stars.length)];
      const e = Math.floor(r() * ENTITIES);
      parts.push({ s, via, e, t: 0, dur: 1.4 + r() * 0.9, hue: s.hue });
    };
    const bez = (a, b, c, t) => ({
      x: (1 - t) * (1 - t) * a.x + 2 * (1 - t) * t * b.x + t * t * c.x,
      y: (1 - t) * (1 - t) * a.y + 2 * (1 - t) * t * b.y + t * t * c.y,
    });
    const glow = (x, y, rad, c, a) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, rad);
      g.addColorStop(0, c); g.addColorStop(1, 'transparent');
      ctx.globalAlpha = a; ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, rad, 0, Math.PI * 2); ctx.fill(); ctx.globalAlpha = 1;
    };

    const step = (dt, time) => {
      spawnT -= dt;
      while (spawnT <= 0) { spawn(); spawnT += 0.09 + r() * 0.12; }
      for (const p of parts) p.t += dt / p.dur;
      for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i].t >= 1) {
          const e = ents[parts[i].e]; e.pulse = 1; e.risk = Math.min(1, e.risk + 0.012 + r() * 0.02);
          parts.splice(i, 1);
        }
      }
      for (const e of ents) { e.pulse = Math.max(0, e.pulse - dt * 1.6); e.hot = Math.max(0, e.hot - dt * 0.35); e.risk = Math.max(0.12, e.risk - dt * 0.012); }
      fireT -= dt;
      if (fireT <= 0) {
        const e = ents.reduce((a, b) => (b.risk > a.risk ? b : a));
        e.hot = 1; e.risk = 0.25; beams.push({ e, t: 0 }); fireT = 4.5 + r() * 2.5;
      }
      for (const b of beams) b.t += dt / 1.8;
      for (let i = beams.length - 1; i >= 0; i--) if (beams[i].t >= 1) beams.splice(i, 1);
      for (const s of stars) s.p += dt * (0.6 + s.s * 0.3);
      return time;
    };

    const draw = () => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);
      const aMul = col.light ? 0.8 : 1;
      // constellation lines
      ctx.strokeStyle = col.line; ctx.lineWidth = 1;
      for (let i = 0; i < stars.length; i++) {
        for (let j = i + 1; j < stars.length; j++) {
          const dx = stars[i].x - stars[j].x; const dy = stars[i].y - stars[j].y; const d2 = dx * dx + dy * dy;
          if (d2 < 3600) { ctx.globalAlpha = (1 - d2 / 3600) * 0.9; ctx.beginPath(); ctx.moveTo(stars[i].x, stars[i].y); ctx.lineTo(stars[j].x, stars[j].y); ctx.stroke(); }
        }
      }
      ctx.globalAlpha = 1;
      // links source -> field, field -> entity (faint)
      for (const s of src) { ctx.strokeStyle = col[s.hue]; ctx.globalAlpha = 0.14; ctx.beginPath(); ctx.moveTo(s.x, s.y); ctx.quadraticCurveTo(W * 0.3, s.y, W * 0.44, H / 2); ctx.stroke(); }
      for (const e of ents) { ctx.strokeStyle = col[1]; ctx.globalAlpha = 0.12; ctx.beginPath(); ctx.moveTo(W * 0.5, H / 2); ctx.quadraticCurveTo(W * 0.62, e.y, e.x, e.y); ctx.stroke(); }
      ctx.globalAlpha = 1;
      // stars
      for (const s of stars) {
        const tw = 0.55 + 0.45 * Math.sin(s.p);
        ctx.fillStyle = col[5]; ctx.globalAlpha = (0.35 + 0.5 * tw) * aMul;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.s, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1;
      // particles
      for (const p of parts) {
        const e = ents[p.e];
        const pt = bez(p.s, p.via, e, p.t);
        glow(pt.x, pt.y, 9, col[p.hue], 0.55 * aMul);
        ctx.fillStyle = col[p.hue]; ctx.beginPath(); ctx.arc(pt.x, pt.y, 2.2, 0, Math.PI * 2); ctx.fill();
      }
      // beams: entity -> trigger -> action
      for (const b of beams) {
        const k = b.t; ctx.lineWidth = 2; ctx.strokeStyle = col[2];
        ctx.globalAlpha = (1 - k) * 0.9; ctx.beginPath(); ctx.moveTo(b.e.x, b.e.y); ctx.lineTo(trig.x, trig.y); ctx.lineTo(act.x, act.y); ctx.stroke();
        ctx.globalAlpha = 1; ctx.lineWidth = 1;
        const q = k < 0.5 ? { x: b.e.x + (trig.x - b.e.x) * k * 2, y: b.e.y + (trig.y - b.e.y) * k * 2 }
          : { x: trig.x + (act.x - trig.x) * (k - 0.5) * 2, y: trig.y + (act.y - trig.y) * (k - 0.5) * 2 };
        glow(q.x, q.y, 16, col[2], 0.9 * aMul);
      }
      // sources
      ctx.font = '600 12.5px Inter, system-ui, sans-serif'; ctx.textBaseline = 'middle';
      for (const s of src) {
        glow(s.x, s.y, 16, col[s.hue], 0.45 * aMul);
        ctx.fillStyle = col[s.hue]; ctx.beginPath(); ctx.arc(s.x, s.y, 4.5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = col.text; ctx.textAlign = 'left'; ctx.fillText(s.label, s.x + 11, s.y);
      }
      // entities
      for (const e of ents) {
        const c = e.hot > 0 ? col[2] : col[1];
        glow(e.x, e.y, 38 + e.pulse * 12 + e.hot * 24, c, (0.35 + e.hot * 0.4) * aMul);
        ctx.strokeStyle = col.line; ctx.lineWidth = 3; ctx.beginPath(); ctx.arc(e.x, e.y, 17, 0, Math.PI * 2); ctx.stroke();
        ctx.strokeStyle = c; ctx.beginPath(); ctx.arc(e.x, e.y, 17, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * e.risk); ctx.stroke();
        ctx.lineWidth = 1;
        if (e.pulse > 0) { ctx.strokeStyle = c; ctx.globalAlpha = e.pulse * 0.7; ctx.beginPath(); ctx.arc(e.x, e.y, 17 + (1 - e.pulse) * 16, 0, Math.PI * 2); ctx.stroke(); ctx.globalAlpha = 1; }
        ctx.fillStyle = c; ctx.beginPath(); ctx.arc(e.x, e.y, 4, 0, Math.PI * 2); ctx.fill();
      }
      // trigger and action
      const hot = beams.length ? 1 : 0;
      ctx.save(); ctx.translate(trig.x, trig.y); ctx.rotate(Math.PI / 4);
      ctx.strokeStyle = col[2]; ctx.globalAlpha = 0.5 + hot * 0.5; ctx.lineWidth = 2; ctx.strokeRect(-9, -9, 18, 18); ctx.restore();
      ctx.globalAlpha = 1; ctx.lineWidth = 1;
      glow(act.x, act.y, 22, col[1], (0.25 + hot * 0.4) * aMul);
      ctx.strokeStyle = col[1]; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(act.x, act.y, 10, 0, Math.PI * 2); ctx.stroke(); ctx.lineWidth = 1;
      ctx.fillStyle = col.muted; ctx.textAlign = 'center'; ctx.font = '700 11px Inter, system-ui, sans-serif';
      ctx.fillText('TRIGGER', trig.x, trig.y - 22); ctx.fillText('ACTION', act.x, act.y + 24);
      ctx.fillText('SOURCES', src[0].x + 22, 12); ctx.fillText('SIGNALS', W * 0.44, 12); ctx.fillText('ENTITIES', ents[0].x, 12);
    };

    let raf = 0; let visible = true;
    const frame = (t) => {
      const dt = Math.min(0.05, (t - last) / 1000); last = t;
      step(dt, t); draw();
      raf = requestAnimationFrame(frame);
    };
    const start = () => { if (!raf && visible && !reduced && !document.hidden) { last = performance.now(); raf = requestAnimationFrame(frame); } };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };

    if (reduced) { for (let i = 0; i < 140; i++) step(0.03, 0); fireT = 99; draw(); } else { for (let i = 0; i < 60; i++) step(0.03, 0); draw(); start(); }

    const ro = new ResizeObserver(() => { layout(); if (reduced || !raf) draw(); });
    ro.observe(wrap);
    const io = new IntersectionObserver((es) => { visible = es.some((e) => e.isIntersecting); if (visible) start(); else stop(); });
    io.observe(wrap);
    const vis = () => { if (document.hidden) stop(); else start(); };
    document.addEventListener('visibilitychange', vis);
    return () => { stop(); ro.disconnect(); io.disconnect(); document.removeEventListener('visibilitychange', vis); };
  }, [theme]);

  return (
    <div className="kz-constellation" ref={wrapRef} role="img"
      aria-label="Illustration: items from email, tickets, chat, logs, orders and invoices flow through a field of signals to the entities they concern; when an entity's pattern holds, a trigger fires an action.">
      <canvas ref={canvasRef} aria-hidden="true" />
    </div>
  );
}
